import React, { useState, useEffect } from "react";
import { AddButton } from "../../../components/common/GeneralComponent";
import { fetchData, formatDateDMY } from "../../../utils/functions";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  Upload,
  FileText,
  Trash,
  Download,
  AlertTriangle,
  CheckCircle,
} from "lucide-react";
import { getGrantTypeConfig } from "../grantTypeConfig";

const Attachments = ({
  grantId,
  onSectionSaved,
  isEditMode,
  showFinalSubmit,
  allFormData,
  onGrantUpdated,
  grantType = "sme",
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const cfg = getGrantTypeConfig(grantType);
  const [loading, setLoading] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [pendingFiles, setPendingFiles] = useState({});

  const API_BASE_URL =
    process.env.REACT_APP_BASE_URL || "http://localhost:8000";

  const attachmentTypes = [
    { value: "CNIC_Copy", label: "CNIC", essential: false },
    { value: "application", label: "Application", essential: false },
    { value: "due_diligence", label: "Due Diligence", essential: false },
    { value: "proposals", label: "Proposal", essential: false },
    { value: "evaluation_sheets", label: "Evaluation Sheet", essential: false },
    { value: "pitching_result", label: "Pitching Result", essential: false },
    { value: "Grant_Agreement", label: "Grant Agreement", essential: false },
    { value: "milestones_movs", label: "Milestone MOVs", essential: false },
    { value: "Photos", label: "Photos (Equipment/Business Site)", essential: false },
  ];

  const sectionRequiredFields = {
    administrativeInfo: ["district_id"],
    beneficiaryDetails: [
      "application_id",
      "company_name",
      "applicant_full_name",
      "cnic_number",
      "gender",
      "contact_number",
      "address",
      // "trade_business",
      "business_description",
    ],
    grantDetails: [
      "project_cost_pkr",
      "project_cost_euro",
      "applicant_share_pkr",
      "applicant_share_euro",
      "unido_grant_share_pkr",
      "unido_grant_share_euro",
      // "business_type",
      "business_entity",
    ],
    milestonesProgress: [],
    verificationSupport: [],
    overallProgress: [],
  };

  useEffect(() => {
    if (grantId) {
      fetchAttachments();
    }
  }, [grantId]);

  const fetchAttachments = async () => {
    try {
      const response = await fetchData(
        `${cfg.apiBase}/${grantId}`,
        "GET",
        null,
        {},
        {},
        navigate,
        "",
        dispatch
      );

      if (response?.data?.attachments) {
        setAttachments(response.data.attachments);
      }
    } catch (error) {
      console.error("Error fetching attachments:", error);
    }
  };

  const flattenFormData = (allFormData) => {
    const flattened = {};

    Object.keys(allFormData).forEach((sectionName) => {
      const sectionData = allFormData[sectionName];

      if (sectionData && typeof sectionData === "object") {
        if (
          sectionName === "milestonesProgress" &&
          Array.isArray(sectionData)
        ) {
          flattened.has_milestones = sectionData.length > 0;
        } else if (!Array.isArray(sectionData)) {
          Object.keys(sectionData).forEach((key) => {
            const value = sectionData[key];
            if (
              value !== null &&
              value !== undefined &&
              value !== "" &&
              !key.includes("_file") &&
              (Array.isArray(value) || typeof value !== "object")
            ) {
              flattened[key] = value;
            }
          });
        }
      }
    });

    const defaultValues = {
      overall_status: "In progress",
    };

    Object.keys(defaultValues).forEach((key) => {
      if (!flattened[key]) {
        flattened[key] = defaultValues[key];
      }
    });

    return flattened;
  };

  const validateFiles = (attachmentType, files) => {
    const filesToCheck =
      attachmentType === "Photos" ? Array.from(files) : [files[0]];
    const validFiles = [];

    const allowedTypes = {
      Photos: ["image/jpeg", "image/jpg", "image/png", "image/gif"],
      default: [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "image/jpeg",
        "image/jpg",
        "image/png",
      ],
    };

    for (const file of filesToCheck) {
      if (file.size > 1 * 1024 * 1024) {
        toast.error(`File ${file.name} is too large. Maximum size is 1MB.`);
        continue;
      }
      const validTypes = allowedTypes[attachmentType] || allowedTypes.default;
      if (!validTypes.includes(file.type)) {
        toast.error(
          `Invalid file type for ${file.name}. Please upload PDF, Word, or image files.`
        );
        continue;
      }
      validFiles.push(file);
    }

    return validFiles;
  };

  const handleFileUpload = async (attachmentType, files) => {
    if (!files || files.length === 0) return;

    const validFiles = validateFiles(attachmentType, files);
    if (validFiles.length === 0) return;

    if (!grantId) {
      setPendingFiles((prev) => ({
        ...prev,
        [attachmentType]: [
          ...(prev[attachmentType] || []),
          ...validFiles,
        ],
      }));
      toast.success(
        `${validFiles.length} file(s) selected.`
      );
      return;
    }

    setUploadingFiles((prev) => ({ ...prev, [attachmentType]: true }));
    try {
      let successfulUploads = 0;

      for (const file of validFiles) {
        const formData = new FormData();
        formData.append(attachmentType, file);
        formData.append("attachment_type", attachmentType);

        await fetchData(
          `${cfg.apiBase}/${grantId}/attachments`,
          "POST",
          formData,
          {},
          {},
          navigate,
          "",
          dispatch
        );

        successfulUploads++;
      }

      if (successfulUploads > 0) {
        toast.success(`${successfulUploads} file(s) uploaded successfully`);
        await fetchAttachments();
        if (isEditMode) {
          setHasUnsavedChanges(true);
        }
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      toast.error("Failed to upload file(s)");
    } finally {
      setUploadingFiles((prev) => ({ ...prev, [attachmentType]: false }));
    }
  };

  const handleDeleteAttachment = async (attachmentId, fileName) => {
    if (!window.confirm(`Are you sure you want to delete ${fileName}?`)) {
      return;
    }

    try {
      await fetchData(
        `${cfg.apiBase}/attachments/${Number(attachmentId)}`,
        "DELETE",
        null,
        {},
        {},
        navigate,
        "",
        dispatch
      );

      toast.success("Attachment deleted successfully");
      fetchAttachments();

      // Mark that there are unsaved changes in edit mode
      if (isEditMode) {
        setHasUnsavedChanges(true);
      }
    } catch (error) {
      console.error("Error deleting attachment:", error);
      toast.error("Failed to delete attachment");
    }
  };

  const handleUpdateAttachments = async () => {
    setLoading(true);
    try {
      if (grantId) {
        await fetchAttachments();
        if (onGrantUpdated) {
          onGrantUpdated();
        }
        toast.success("Attachments section updated successfully");
        setHasUnsavedChanges(false);
      }
    } catch (error) {
      console.error("Error updating attachments:", error);
      toast.error("Failed to update attachments section");
    } finally {
      setLoading(false);
    }
  };

  const getAttachmentsByType = (type) => {
    return attachments.filter((att) => att.attachment_type === type);
  };

  const validateSectionData = (sectionName, data) => {
    const requiredFields = sectionRequiredFields[sectionName] || [];

    if (sectionName === "milestonesProgress") {
      return data && Array.isArray(data) && data.length > 0;
    }

    if (!data || typeof data !== "object") {
      return requiredFields.length === 0;
    }

    return requiredFields.every((field) => {
      const value = data[field];
      return value !== null && value !== undefined && value !== "";
    });
  };

  const getSectionValidationStatus = () => {
    const status = {};

    Object.keys(sectionRequiredFields).forEach((sectionName) => {
      const sectionData = allFormData[sectionName];
      status[sectionName] = validateSectionData(sectionName, sectionData);
    });

    return status;
  };

  const createMilestonesForGrant = async (grantId, milestonesData) => {
    if (
      !milestonesData ||
      !Array.isArray(milestonesData) ||
      milestonesData.length === 0
    ) {
      return true; // No milestones to create is valid
    }

    try {

      const includeSevenPercentAdvance = milestonesData.some(
        (m) => Number(m.milestone_number) === 0
      );
      const realMilestones = milestonesData
        .filter((m) => Number(m.milestone_number) !== 0)
        .map((milestone, index) => ({
          id: milestone.id || null,
          milestone_number: index + 1,
          milestone_title: milestone.milestone_title?.trim() || "",
          documents_required: milestone.documents_required || null,
          amount_pkr: parseFloat(milestone.amount_pkr) || 0,
          amount_euro: parseFloat(milestone.amount_euro) || 0,
          amount_released_pkr:
            milestone.amount_released_pkr === "" ||
            milestone.amount_released_pkr === undefined
              ? null
              : parseFloat(milestone.amount_released_pkr),
          amount_released_euro:
            milestone.amount_released_euro === "" ||
            milestone.amount_released_euro === undefined
              ? null
              : parseFloat(milestone.amount_released_euro),
          deadline_date: milestone.deadline_date || null,
          status: milestone.status || "In progress",
          remarks: milestone.remarks || null,
        }));
      const advanceMilestone = milestonesData.find(
        (m) => Number(m.milestone_number) === 0
      );
      const formattedMilestones = includeSevenPercentAdvance
        ? [
            {
              id: advanceMilestone.id || null,
              milestone_number: 0,
              milestone_title: "7% Advance",
              documents_required: null,
              amount_pkr: parseFloat(advanceMilestone.amount_pkr) || 0,
              amount_euro: parseFloat(advanceMilestone.amount_euro) || 0,
              amount_released_pkr:
                advanceMilestone.amount_released_pkr === "" ||
                advanceMilestone.amount_released_pkr === undefined
                  ? null
                  : parseFloat(advanceMilestone.amount_released_pkr),
              amount_released_euro:
                advanceMilestone.amount_released_euro === "" ||
                advanceMilestone.amount_released_euro === undefined
                  ? null
                  : parseFloat(advanceMilestone.amount_released_euro),
              deadline_date: null,
              status: advanceMilestone.status || "In progress",
              remarks: null,
            },
            ...realMilestones,
          ]
        : realMilestones;

      for (let i = 0; i < formattedMilestones.length; i++) {
        const milestone = formattedMilestones[i];
        if (milestone.milestone_number === 0) continue;
        if (!milestone.milestone_title) {
          console.error(`Milestone ${i + 1}: Title is required`);
          return false;
        }
        if (milestone.amount_pkr <= 0) {
          console.error(
            `Milestone ${i + 1}: PKR amount must be greater than 0`
          );
          return false;
        }
        if (milestone.amount_euro <= 0) {
          console.error(
            `Milestone ${i + 1}: Euro amount must be greater than 0`
          );
          return false;
        }
      }

      // Use the dedicated saveMilestones endpoint with error handling
      const response = await fetchData(
        `${cfg.apiBase}/${grantId}/milestones/save`,
        "PUT",
        {
          milestones: formattedMilestones,
          include_seven_percent_advance: includeSevenPercentAdvance,
          seven_percent_adjust_milestone: includeSevenPercentAdvance
            ? Number(advanceMilestone?.adjust_at_milestone) || null
            : null,
        },
        {},
        {},
        navigate,
        "",
        dispatch
      );

      // Check response more thoroughly
      if (
        response &&
        (response.success === true ||
          response.message === "Milestones saved successfully")
      ) {
   
        return true;
      } else {
        console.error("Failed to create milestones:", response);
        toast.error("Failed to create milestones");
        return false;
      }
    } catch (error) {
      console.error("Error creating milestones:", error);
      toast.error("Error creating milestones: " + error.message);
      return false;
    }
  };

  const createGrantWithAllData = async () => {
    try {
      const formData = new FormData();
      const flattenedData = flattenFormData(allFormData);
      formData.append("grant_type", cfg.grantTypeValue);

      Object.keys(flattenedData).forEach((key) => {
        if (
          key !== "has_milestones" &&
          !key.includes("_file") &&
          flattenedData[key] !== null &&
          flattenedData[key] !== undefined &&
          flattenedData[key] !== ""
        ) {
          formData.append(
            key,
            Array.isArray(flattenedData[key])
              ? JSON.stringify(flattenedData[key])
              : flattenedData[key]
          );
        }
      });

      const businessProposalFile =
        allFormData?.beneficiaryDetails?.business_proposal_file;
      const qualificationMinutesFile =
        allFormData?.grantDetails?.qualification_minutes_file;
      const verificationReportFile =
        allFormData?.verificationSupport?.verification_report_file;
      const spotCheckReportFile =
        allFormData?.verificationSupport?.spot_check_report_file;

      if (businessProposalFile instanceof File) {
        formData.append("business_proposal_file", businessProposalFile);
      }

      if (qualificationMinutesFile instanceof File) {
        formData.append(
          "qualification_minutes_file",
          qualificationMinutesFile
        );
      }

      if (verificationReportFile instanceof File) {
        formData.append("verification_report_file", verificationReportFile);
      }

      if (spotCheckReportFile instanceof File) {
        formData.append("spot_check_report_file", spotCheckReportFile);
      }

      for (let pair of formData.entries()) {

      }

      const response = await fetchData(
        `${cfg.apiBase}/with-files`,
        "POST",
        formData,
        {},
        {},
        navigate,
        "",
        dispatch
      );

      const newGrantId = response?.grant?.grant_id;

      if (!newGrantId) {
        throw new Error("Failed to create grant - no grant ID returned");
      }

      return newGrantId;
    } catch (error) {
      console.error("Error creating grant:", error);
      throw error;
    }
  };

  const uploadFilesForNewGrant = async (newGrantId) => {
    const types = Object.keys(pendingFiles);
    for (const attachmentType of types) {
      for (const file of pendingFiles[attachmentType]) {
        const formData = new FormData();
        formData.append(attachmentType, file);
        formData.append("attachment_type", attachmentType);
        await fetchData(
          `${cfg.apiBase}/${newGrantId}/attachments`,
          "POST",
          formData,
          {},
          {},
          navigate,
          "",
          dispatch
        );
      }
    }
  };

  const handleFinalSubmit = async () => {
    setLoading(true);
    try {
      const validationStatus = getSectionValidationStatus();
      const invalidSections = Object.entries(validationStatus)
        .filter(([section, isValid]) => !isValid)
        .map(([section]) => section);

      if (invalidSections.length > 0) {
        const sectionLabels = {
          administrativeInfo: "Administrative Info",
          beneficiaryDetails: "Beneficiary Details",
          grantDetails: "Grant Details",
          milestonesProgress: "Milestones Progress",
          verificationSupport: "Verification & Support",
          overallProgress: "Overall Progress",
        };

        const invalidLabels = invalidSections
          .map((section) => sectionLabels[section])
          .join(", ");
        toast.error(
          `Please complete the following sections before submitting: ${invalidLabels}`
        );
        setLoading(false);
        return;
      }

      let finalGrantId = grantId;

      // Create grant if it doesn't exist
      if (!finalGrantId) {
        finalGrantId = await createGrantWithAllData();
        if (!finalGrantId) {
          toast.error("Failed to create grant");
          setLoading(false);
          return;
        }

        // Update the parent component with the new grant ID
        if (window.updateGrantId) {
          window.updateGrantId(finalGrantId);
        }

        // Upload files after grant creation
        await uploadFilesForNewGrant(finalGrantId);

        // Add a small delay to ensure the grant is fully created
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }

      // Create milestones AFTER the grant is confirmed to exist
      if (
        allFormData.milestonesProgress &&
        Array.isArray(allFormData.milestonesProgress) &&
        allFormData.milestonesProgress.length > 0
      ) {

        const milestonesSuccess = await createMilestonesForGrant(
          finalGrantId,
          allFormData.milestonesProgress
        );

        if (!milestonesSuccess) {
          toast.warning(
            "Grant created but failed to save some milestones. You can add them later in edit mode."
          );
        } else {
        }
      }

      toast.success(
        `${cfg.label} created successfully with all data and attachments!`
      );
      if (onSectionSaved) onSectionSaved();
    } catch (error) {
      console.error("Error finalizing grant creation:", error);
      toast.error("Error finalizing grant creation: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const getSectionSummary = () => {
    const validationStatus = getSectionValidationStatus();
    const sectionLabels = {
      administrativeInfo: "Administrative Info",
      beneficiaryDetails: "Beneficiary Details",
      grantDetails: "Grant Details",
      milestonesProgress: "Milestones Progress",
      verificationSupport: "Verification & Support",
      overallProgress: "Overall Progress",
    };

    return Object.entries(validationStatus).map(([section, isValid]) => ({
      section,
      label: sectionLabels[section],
      isValid,
      hasData:
        allFormData[section] &&
        (Array.isArray(allFormData[section])
          ? allFormData[section].length > 0
          : Object.keys(allFormData[section]).length > 0),
    }));
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {attachmentTypes.map((attachmentType, typeIndex) => {
          const typeAttachments = getAttachmentsByType(attachmentType.value);
          const typePending = pendingFiles[attachmentType.value] || [];
          const fileCount = typeAttachments.length + typePending.length;
          const isUploading = uploadingFiles[attachmentType.value];
          const isPhotos = attachmentType.value === "Photos";

          return (
            <div
              key={attachmentType.value}
              className="flex flex-col rounded-lg border border-[#e5e7eb] bg-white"
            >

              <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-[#f3f4f6] bg-[#f9fafb] rounded-t-lg">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#137e81] text-white text-xs font-semibold flex items-center justify-center">
                    {typeIndex + 1}
                  </span>
                  <h3 className="font-medium text-[#374151] truncate" title={attachmentType.label}>
                    {attachmentType.label}
                  </h3>
                  {fileCount > 0 && (
                    <span className="flex-shrink-0 text-xs text-[#6b7280]">({fileCount})</span>
                  )}
                  {attachmentType.essential && (
                    <span className="flex-shrink-0 bg-red-100 text-red-800 text-xs px-2 py-0.5 rounded">
                      Essential
                    </span>
                  )}
                </div>
                <label
                  htmlFor={`file-${attachmentType.value}`}
                  className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-sm bg-[#137e81] text-white rounded-md cursor-pointer hover:bg-[#0f6366] ${
                    isUploading ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                >
                  <Upload size={14} />
                  <span>{isUploading ? "Uploading..." : "Upload"}</span>
                </label>
                <input
                  id={`file-${attachmentType.value}`}
                  type="file"
                  multiple={isPhotos}
                  accept={isPhotos ? "image/*" : ".pdf,.doc,.docx,image/*"}
                  onChange={(e) => {
                    handleFileUpload(attachmentType.value, e.target.files);
                    // allow choosing the same file again after removing it
                    e.target.value = "";
                  }}
                  disabled={isUploading}
                  className="hidden"
                />
              </div>

              {/* Files */}
              <div className="flex-1 p-3 space-y-2">
                {fileCount > 0 ? (
                  <>
                    {typeAttachments.map((attachment) => (
                      <div
                        key={attachment.attachment_id}
                        className="flex items-center justify-between gap-2 p-2 bg-[#f9fafb] rounded border"
                      >
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <FileText size={18} className="text-[#6b7280] flex-shrink-0" />
                          <div className="min-w-0">
                            <p
                              className="text-sm font-medium text-[#374151] truncate"
                              title={attachment.file_name}
                            >
                              {attachment.file_name}
                            </p>
                            <p className="text-xs text-[#6b7280]">
                              Uploaded: {formatDateDMY(attachment.uploaded_at)}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <button
                            type="button"
                            onClick={() =>
                              window.open(
                                `${API_BASE_URL}${attachment.file_path}`,
                                "_blank"
                              )
                            }
                            className="text-blue-600 hover:text-blue-800"
                            title="View/Download"
                          >
                            <Download size={16} />
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              handleDeleteAttachment(
                                attachment.attachment_id,
                                attachment.file_name
                              )
                            }
                            className="text-red-600 hover:text-red-800"
                            title="Delete"
                          >
                            <Trash size={16} />
                          </button>
                        </div>
                      </div>
                    ))}
                    {typePending.map((file, idx) => (
                      <div
                        key={`pending-${idx}`}
                        className="flex items-center justify-between gap-2 p-2 bg-yellow-50 rounded border border-yellow-200"
                      >
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <FileText size={18} className="text-yellow-600 flex-shrink-0" />
                          <p
                            className="text-sm font-medium text-[#374151] truncate"
                            title={file.name}
                          >
                            {file.name}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() =>
                            setPendingFiles((prev) => ({
                              ...prev,
                              [attachmentType.value]: prev[
                                attachmentType.value
                              ].filter((_, i) => i !== idx),
                            }))
                          }
                          className="text-red-600 hover:text-red-800 flex-shrink-0"
                          title="Remove"
                        >
                          <Trash size={16} />
                        </button>
                      </div>
                    ))}
                  </>
                ) : (
                  <div className="flex items-center justify-center gap-2 py-4 text-sm text-[#9ca3af]">
                    <FileText size={18} className="opacity-60" />
                    <span>No files uploaded yet</span>
                  </div>
                )}
              </div>

              <p className="px-4 pb-3 text-xs text-[#9ca3af]">
                {isPhotos
                  ? "JPG, PNG, GIF · max 1MB each · multiple files allowed"
                  : "PDF, DOC, DOCX or image · max 1MB"}
              </p>
            </div>
          );
        })}
      </div>

      <div className="pt-4 flex justify-end gap-3">
        {/* Update button for edit mode */}
        {isEditMode && grantId && (
          <AddButton
            text="Update Attachments"
            type="button"
            onClick={handleUpdateAttachments}
            isLoading={loading}
            disabled={loading}
          />
        )}

        {/* Final submit button for create mode */}
        {showFinalSubmit && !isEditMode && (
          <AddButton
            text="Complete Grant Creation"
            type="button"
            onClick={handleFinalSubmit}
            isLoading={loading}
            disabled={loading}
          />
        )}
      </div>
    </div>
  );
};

export default Attachments;